// Gravity's pulling set, the flight frame it defines, the frame-parent carry,
// ground contact, the path recorder and the launch/land verbs.
import * as THREE from 'three';
import { bodyById, isShapeBodyRow } from '../world/bodies';
import type { BodyRow } from '../world/types';
import { heliocentric } from '../world/ephemeris';
import { gameState } from '../state/gameState';
import { gameClock, REAL_TIME_RATE } from '../world/clock';
import {
  createFlightState, flightSpeed, seatOnGround,
  type FlightAxes, type FlightConfig, type FlightState, type FlightVec,
} from '../sim/flight';
import { createFlightCentres, stepFlightSpans, type FlightFrameContext } from '../sim/flightCentres';
import { createGravityField, type GravityField } from '../sim/gravityField';
import { bodySpinRateAt, spinAheadDir } from '../sim/ground';
import type { BodyPlaces } from '../sim/bodies';
import type { SimPositionHandle } from '../sim/position';
import type { WarpHandle } from '../sim/warp';
import { preHandoff, type AtlasWorldHandle, type LandVerbHandle } from '../sim/landVerb';
import { createPathRecorder, type PathRecorder } from '../sim/pathRecorder';
import { isMode } from '../appMode';
import { formatDistanceForHud, formatMpsForHud } from '../pilotPanel';
import type { Custos } from '../scene/custos';
import type { Engagement } from '../game/engagement';
import type { PilotTarget } from './pilotTarget';
import type { WorldVec } from '../world/frame';
import { urlParam } from '../urlFlags';
import { TEST_SWITCHES } from '../urlFlags.ts';

/** The reassigned module `let`s in `../main.ts` this field reads and, for
 *  `seedJd`/`seedJdPrev`, advances every frame. */
export interface FlightFieldLive {
  readonly simBodies: BodyPlaces | null;
  readonly bodyRows: readonly BodyRow[];
  readonly bootJd: number;
  seedJd: number;
  seedJdPrev: number;
  /** `drive.ts`'s own accumulator; read here only for the path recorder's rows. */
  readonly flightSimSeconds: number;
}

export interface FlightFieldDeps {
  simPos: SimPositionHandle;
  bubble: THREE.Group;
  ship: Custos;
  /** The Atlas's own drawn-ground reader; mounted by the Atlas host, so a
   *  hoisted function in `../main.ts` that reads the current mount off
   *  `scene.userData`. */
  atlasWorld(): AtlasWorldHandle | null;
  /** The ship's transform off the authoritative doubles; still in `../main.ts`. */
  syncShipTransform(): void;
  /** `../main.ts` builds this after the flight field, so both read it through a
   *  getter deferred past that point. */
  warp: WarpHandle;
  /** Same reason as `warp`: built after the flight field in `../main.ts`. */
  landVerb: LandVerbHandle;
  /** Same reason as `warp`: `pilot.parkInOrbit` is what LAUNCH resolves to. */
  pilot: PilotTarget;
  /** `game.engagement`: whether a fight owns the view, so LAUNCH stands down. */
  game: { engagement: Engagement | null };
  /** `?assistkeepaim=1`: whether a frame handoff carries the commanded aim
   *  across it rather than dropping to the ship's own velocity. */
  assistKeepAim: boolean;
  live: FlightFieldLive;
  turnRate: number;
  accel: number;
  dragOn: boolean;
  dragPerS: number;
  maxSubstepS: number;
  maxSubsteps: number;
  assistFree: boolean;
  assistHeldOnly: boolean;
  assistFrozenAim: boolean;
  assistNoFloor: boolean;
  dropLiftoffSpin: boolean;
  inertialHeading: boolean;
  /** `?assistrealonly=1` / `?assistrealsecs=1`: how `assistSeconds`/`assistLive` read the clock. */
  assistRealOnly: boolean;
  assistRealSecs: boolean;
  gravityOn: boolean;
  oneBody: boolean;
  helioFrame: boolean;
  centresHeld: boolean;
  /** True mid-fight: `rideFightFrame` stands down for `PLAY_FIGHT_ORDER`'s own step. */
  fightHeld: boolean;
  bodiesPinned: boolean;
  rewindCarry: boolean;
}

export interface FlightFieldHandle {
  flightConfig: FlightConfig;
  flight: FlightState;
  gravityField: GravityField;
  /** `CruiseDriveDeps._frameCtx`: the one `drive.ts`'s own fly() steps every frame. */
  _frameCtx: FlightFrameContext;
  assistSeconds(dt: number, realDt: number): number;
  assistLive(): boolean;
  stepFlightFrame(
    axes: FlightAxes, throttle: number, dt: number, attitudeDt: number,
    substeps: number, out: FlightVec, assistDt: number,
  ): void;
  advanceSimBodies(): void;
  frameParentStep(): FlightVec;
  rideFightFrame(): void;
  placeFrameEpoch(): void;
  adoptFlightFrame(id: string | null): void;
  frameParentWas: string | null;
  seedOnFit: { parentId: string; dvSpent: number } | null;
  rebaseFlightFrame(): void;
  /** The drawn ground's radius from a row's centre along a direction, with the
   *  seat's own resting-ground cache folded in; `pilotTarget.ts`'s own dep. */
  contactRadiusM(row: BodyRow, dx: number, dy: number, dz: number): number;
  noteStopLateSag(dtS: number): void;
  noteGroundContact(): void;
  pathRecorder: PathRecorder;
  /** Written by `simFlight.recordPath`'s own arm; read by `pathValues` and
   *  `notePathRow`. */
  pathIds: readonly string[];
  pathValues(): number[] | null;
  notePathRow(): void;
  hullReachM(dir: THREE.Vector3): number;
  seatShipOnDrawnSurface(bodyId: string): void;
  groundSeatReading(): GroundSeatReading | null;
  noteContactReading(bodyId: string, speedMps: number, contactRadiusM: number): void;
  readonly lastContactReading: ContactReading | null;
  groundUnderReading(bodyId: string, dir: FlightVec | null):
  { shipRadiusM: number; drawnRadiusM: number; contactRadiusM: number | null } | null;
  attemptLaunch(): void;
  shipWorldAt(): WorldVec;
  shipWorldVelocity(): FlightVec;
  /** `slotSurfaceRadiusM` as `simFlight.slotSurface` (the DEV debug read) calls
   *  it, by the row's own pull slot. */
  slotSurfaceRadiusM(i: number, dx: number, dy: number, dz: number, leadS: number): number;
  /** The direction `slotSurfaceRadiusM` last turned back by the row's spin,
   *  for `simFlight.slotSurface` to echo. */
  readonly slotSurfaceDir: FlightVec;
}

/** Where a resting ship sits against the globe as it is drawn: the radius it
 *  rests at, the radius the mesh carries under it, how far the hull reaches
 *  below its own origin, and what is left between the keel and that surface.
 *  Null while the ship is flying. Read by `tools/preview/grounded-view-check.mjs`. */
export interface GroundSeatReading {
  bodyId: string;
  shipRadiusM: number;
  drawnRadiusM: number;
  hullReachM: number;
  keelFloatM: number;
}

export interface ContactReading {
  bodyId: string;
  speedMps: number;
  /** The ship's origin from the body's centre where the sweep stopped it. */
  contactRadiusM: number;
  /** The seat's own reader along that direction. */
  drawnRadiusM: number;
  /** The origin from the centre once the seat has put the hull down. */
  seatedRadiusM: number;
  hullReachM: number;
}

/** The key LAUNCH is on, named in the notice and in the pilot panel's own row. */
export const LAUNCH_KEY = 'O';
/**
 * Metres over the surface LAUNCH puts the ship back at. `parkInOrbit` carries
 * no default of its own, and this is the altitude its own docstring works its
 * out-of-plane offset at.
 */
export const LAUNCH_ALTITUDE_M = 300_000;

// `?datumspherecontact=1`: the ground sweep, HEIGHT, DISTANCE and CLOSEST back
// on the registry's `radius_m` sphere on a shape-model body, which is the
// surface they met before the drawn one reached them. The seat still puts the
// hull down on the drawn ground, so the panel reads kilometres of height off a
// ship visibly sitting on it. `ground-contact-check.mjs --inject
// datum-sphere-contact` is the same state under Node. Nothing a player sets.
const DATUM_SPHERE_CONTACT
  = TEST_SWITCHES && new URLSearchParams(location.search).get('datumspherecontact') === '1';

// `?datumsweep=1`: on a body drawn as a sphere with relief, the sweep and the
// in-flight HEIGHT back on the datum sphere, so a descent over Luna's highlands
// falls through the drawn ground to it. `touchdown-ground-check.mjs --inject
// datum-sweep` reaches it; nothing a player sets.
const DATUM_SWEEP = TEST_SWITCHES && new URLSearchParams(location.search).get('datumsweep') === '1';

/** `?datumrestheight=1`: a ship resting on a body drawn as a sphere with relief
 *  reads HEIGHT and DISTANCE off the datum again, 6 km on Luna's sunward point.
 *  `landed-frame-check.mjs --inject datum-rest-height` reaches it. */
const DATUM_REST_HEIGHT
  = TEST_SWITCHES && new URLSearchParams(location.search).get('datumrestheight') === '1';

// `?spinpoleatlas=1`: the spin lead turns about the pole in the Atlas's axes
// again, `ground-contact-check.mjs --inject atlas-axes-pole`'s control.
const SPIN_POLE_ATLAS_AXES = TEST_SWITCHES
  && new URLSearchParams(location.search).get('spinpoleatlas') === '1';

/** `?seatinertial=1`: a resting ship held still in the flight frame's axes
 *  while the body turns under it, which is the seat before it turned.
 *  `grounded-view-check.mjs --inject seat-inertial` reaches it; no player does. */
const SEAT_INERTIAL = TEST_SWITCHES && new URLSearchParams(location.search).get('seatinertial') === '1';

/** `?seatondatum=1`: a landing left on the sphere the sweep meets, which is the
 *  seat before this existed. `grounded-view-check.mjs --inject seat-on-datum`
 *  reaches it; nothing a player presses does. */
const SEAT_ON_DATUM = TEST_SWITCHES && new URLSearchParams(location.search).get('seatondatum') === '1';

/** `?stoplate=<frames>`: the surface the sweep meets pulled IN by that many
 *  frames of the ship's own travel, so the stop lands that far inside the
 *  drawn ground and the frames the player sees have the hull in rock. Held to
 *  half the surface so the sweep still meets something to land on.
 *  `grounded-view-check.mjs --inject stop-late` reaches it; no player does. */
const STOP_LATE_FRAMES = (() => {
  const n = TEST_SWITCHES ? Number(urlParam('stoplate') ?? 0) : 0;
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
})();

export function createFlightField(deps: FlightFieldDeps): FlightFieldHandle {
  const {
    simPos, bubble, atlasWorld, syncShipTransform, live, assistKeepAim,
    turnRate, accel, dragOn, dragPerS, maxSubstepS, maxSubsteps,
    assistFree, assistHeldOnly, assistFrozenAim, assistNoFloor,
    dropLiftoffSpin, inertialHeading, assistRealOnly, assistRealSecs,
    gravityOn, oneBody, helioFrame, centresHeld, fightHeld, bodiesPinned, rewindCarry,
  } = deps;

  const flightConfig: FlightConfig = {
    turnRate,
    accel,
    drag: dragOn ? dragPerS : 0,
    maxSubstepS,
    maxSubsteps,
    assistFree,
    assistHeldOnly,
    assistFrozenAim,
    assistNoFloor,
    dropLiftoffSpin,
    inertialHeading,
  };

  /**
   * Seconds the assist may act over this frame, 0 for none. SIMULATED ones: the
   * assist is lateral thrust and rides the substeps gravity and the main engine
   * already ride, so a turn under warp comes round at the same radians per
   * simulated second and is billed the same delta-v as the same turn at real
   * time (`stepFlight` splits this across the substeps; `applyAssist` in
   * src/sim/flight.ts caps each one).
   */
  function assistSeconds(dt: number, realDt: number): number {
    if (!gameState().ship.flight.assistOn) return 0;
    if (assistRealOnly && gameClock().rate !== REAL_TIME_RATE) return 0;
    return assistRealSecs ? realDt : dt;
  }
  /** Is the assist on AND able to act? Only `?assistrealonly=1` says no. */
  function assistLive(): boolean {
    return gameState().ship.flight.assistOn
      && (!assistRealOnly || gameClock().rate === REAL_TIME_RATE);
  }

  const flight = createFlightState();

  /**
   * `isShapeBodyRow` with the boot control in front of it: whether the sweep and
   * the pilot panel read this row's drawn shape off its height field. Phobos's
   * drawn ground runs two kilometres either side of its 11,080 m `radius_m`; a
   * sphere with a DEM reads its drawn globe's facets instead (`reliefRow`).
   */
  function shapeBodyRow(row: BodyRow | null | undefined): boolean {
    if (DATUM_SPHERE_CONTACT) return false;
    return isShapeBodyRow(row);
  }

  /** Whether the sweep reads this row's drawn globe facet by facet: a body drawn
   *  as a sphere with relief rather than as its own shape. */
  function reliefRow(row: BodyRow | null | undefined): boolean {
    return !DATUM_SWEEP && row !== null && row !== undefined && !isShapeBodyRow(row);
  }

  /** The bound the sweep meets before it reads a surface at all: the drawn
   *  globe's own wherever it carries relief, and the datum on everything else. */
  function outerRadiusM(row: BodyRow): number {
    let outer: number | null = null;
    if (shapeBodyRow(row)) outer = atlasWorld()?.shapeOuterRadiusM(row.id) ?? null;
    else if (reliefRow(row)) outer = atlasWorld()?.reliefOuterRadiusM(row.id) ?? null;
    return outer !== null && outer > row.radius_m ? outer : row.radius_m;
  }

  /**
   * Where the drawn ground is under one direction out of a body's centre, metres:
   * the surface the sweep stops on, the seat sits on and HEIGHT reads zero at.
   * On a body drawn as a sphere with relief a ship resting on it reads against
   * the drawn ground its seat put it down on, cached while it rests. The datum
   * sphere wherever there is nothing to read.
   */
  function contactRadiusM(row: BodyRow, dx: number, dy: number, dz: number): number {
    let r: number | null = null;
    if (shapeBodyRow(row)) {
      r = atlasWorld()?.shapeSurfaceRadiusM(row.id, { x: dx, y: dy, z: dz }) ?? null;
    } else if (!isShapeBodyRow(row) && flight.grounded === row.id) {
      if (!DATUM_REST_HEIGHT) r = atlasWorld()?.restingGroundRadiusM(row.id, { x: dx, y: dy, z: dz }) ?? null;
    } else if (reliefRow(row)) {
      r = atlasWorld()?.reliefSurfaceRadiusM(row.id, { x: dx, y: dy, z: dz }) ?? null;
    }
    return r !== null && r > 0 ? r : row.radius_m;
  }

  const _slotSurfaceDir: FlightVec = { x: 0, y: 0, z: 0 };
  let stopLateSagM = 0;

  /**
   * `FlightGround.surfaceAt`: the same reading, by pull slot, turned back by the
   * row's own spin over `leadS` first - `spinAheadDir` (`src/sim/ground.ts`) -
   * since `atlasWorld().shapeSurfaceRadiusM` (a shape-model body's field) and
   * `reliefSurfaceRadiusM` (a sphere with relief's globe facets) read the mesh's CURRENT
   * orientation and a contact minutes out needs the patch of ground that will
   * be under the ship then, not the one under it now. `seedJd` is the flight
   * frame's own current epoch; only the spin term needs `leadS` at all.
   */
  function slotSurfaceRadiusM(
    i: number, dx: number, dy: number, dz: number, leadS: number,
  ): number {
    const id = gravityField.pullIds[i] ?? '';
    if (id === '') return 0;
    const row = gravityField.pullRows[i] ?? null;
    if (row !== null) {
      spinAheadDir(row, live.seedJd, dx, dy, dz, leadS, _slotSurfaceDir, SPIN_POLE_ATLAS_AXES);
    } else {
      _slotSurfaceDir.x = dx; _slotSurfaceDir.y = dy; _slotSurfaceDir.z = dz;
    }
    const world = atlasWorld();
    const r = world?.shapeSurfaceRadiusM(id, _slotSurfaceDir)
      ?? (reliefRow(row) ? world?.reliefSurfaceRadiusM(id, _slotSurfaceDir) : null) ?? 0;
    return r > 0 && stopLateSagM > 0 ? Math.max(r - stopLateSagM, r * 0.5) : r;
  }

  /**
   * `FlightGround.spinBy`: the same pole and rate `slotSurfaceRadiusM` turns the
   * contact's query by, forward rather than back, so a resting ship turns with
   * the ground the sweep stopped it on.
   */
  function slotSpinBy(i: number, d: FlightVec, dtS: number): void {
    if (SEAT_INERTIAL || !(dtS > 0)) return;
    const row = gravityField.pullRows[i] ?? null;
    if (row !== null) spinAheadDir(row, live.seedJd, d.x, d.y, d.z, -dtS, d);
  }

  /**
   * `FlightGround.spinRateAt`: the same pole and rate `slotSpinBy` turns the
   * offset by, as an angular velocity vector, for a liftoff's tangential
   * velocity and a resting ship's own turning orientation (`src/sim/flight.ts`).
   */
  function slotSpinRate(i: number, out: FlightVec): void {
    const row = gravityField.pullRows[i] ?? null;
    if (row === null) { out.x = 0; out.y = 0; out.z = 0; return; }
    bodySpinRateAt(row, live.seedJd, out, SPIN_POLE_ATLAS_AXES);
  }

  // `src/sim/gravityField.ts` owns the pulling set. Called once, here; the
  // destructure below keeps every array and struct name this file already used.
  const gravityField = createGravityField({
    simBodies: () => live.simBodies,
    shipAt: () => simPos.world,
    bodyRows: () => live.bodyRows,
    seedJd: () => live.seedJd,
    seedJdPrev: () => live.seedJdPrev,
    outerRadiusM,
    surfaceAt: slotSurfaceRadiusM,
    spinBy: slotSpinBy,
    spinRateAt: slotSpinRate,
    gravityOn,
    oneBody,
    helioFrame,
  });
  const {
    field: _field, pulls: _pulls, pullPeriodS: _pullPeriodS,
    update: updateFlightField, refill: refillFlightField,
    sampleCentre: sampleFlightCentre, frameAccelAt: fillFrameAccel,
  } = gravityField;

  /**
   * Where every pull centre stands ACROSS the frame, rather than at the one
   * instant the registry was placed at. `src/sim/flightCentres.ts` holds the grid
   * and `src/sim/trajectory.ts` is the prediction's own copy of the same idea.
   */
  const _centres = createFlightCentres();

  /**
   * One frame of flight, flown in spans so the centres move across it.
   * `stepFlightSpans` in `src/sim/flightCentres.ts` is the loop; this fills the
   * module state it reads, so `tools/preview/transit-check.mjs` can fly the same
   * entry off its own registry.
   */
  const _frameCtx: FlightFrameContext = {
    state: flight,
    config: flightConfig,
    field: _field,
    centres: _centres,
    pulls: _pulls,
    pullCount: 0,
    periodS: _pullPeriodS,
    parentIdx: -1,
    jd0: Number.NaN,
    jd1: Number.NaN,
    sample: sampleFlightCentre,
    frameAccelAt: fillFrameAccel,
    centresHeld,
  };
  function stepFlightFrame(
    axes: FlightAxes, throttle: number, dt: number, attitudeDt: number,
    substeps: number, out: FlightVec, assistDt: number,
  ): void {
    _frameCtx.pulls = _pulls;
    _frameCtx.periodS = _pullPeriodS;
    _frameCtx.pullCount = gravityField.pullCount;
    _frameCtx.parentIdx = gravityField.frameParentIdx;
    _frameCtx.jd0 = live.seedJdPrev;
    _frameCtx.jd1 = live.seedJd;
    stepFlightSpans(_frameCtx, axes, throttle, dt, attitudeDt, substeps, out, assistDt);
  }

  /** This frame's worth of that, off the speed the ship is actually carrying. */
  function noteStopLateSag(dtS: number): void {
    if (STOP_LATE_FRAMES <= 0) return;
    stopLateSagM = flightSpeed(flight) * dtS * STOP_LATE_FRAMES;
  }

  // The flown path on a fixed grid of sim seconds, armed only by
  // `simFlight.recordPath`; unarmed it is one property read a cruise frame.
  const pathRecorder = createPathRecorder();
  let pathIds: readonly string[] = [];
  const _pathValues: number[] = [];
  /** The sim instant and body of the latest ground contact, for the recorder alone. */
  let contactSimS = Number.NaN;
  let contactBodyId = '';

  /** A surface was met: drop to real time and name the body, the hit speed and the key off it. */
  function noteGroundContact(): void {
    const contact = gravityField.ground.contact;
    if (!contact.hit) return;
    contact.hit = false;
    // Every caller runs this before the step's seconds reach the clock.
    contactSimS = live.flightSimSeconds + contact.atS;
    contactBodyId = contact.bodyId;
    const row = bodyById(live.bodyRows, contact.bodyId);
    const name = (row?.display_name ?? contact.bodyId).toUpperCase();
    const contactRadius = import.meta.env.DEV ? Math.hypot(
      flight.groundOffset.x, flight.groundOffset.y, flight.groundOffset.z) : 0;
    seatShipOnDrawnSurface(contact.bodyId);
    if (import.meta.env.DEV) noteContactReading(contact.bodyId, contact.speedMps, contactRadius);
    deps.warp.drop('GROUND');
    deps.warp.showNotice(`GROUND: ${name} AT ${formatMpsForHud(contact.speedMps)}`
      + ` - PRESS ${LAUNCH_KEY} TO LAUNCH BACK TO ORBIT`, LAUNCH_KEY);
  }

  /** The ship, then each of `pathIds`, in heliocentric metres; null for a body the registry lacks. */
  function pathValues(): number[] | null {
    const w = shipWorldAt();
    _pathValues.length = 0;
    _pathValues.push(w.x, w.y, w.z);
    for (const id of pathIds) {
      const b = live.simBodies?.worldAt(id);
      if (!b) return null;
      _pathValues.push(b.x, b.y, b.z);
    }
    return _pathValues;
  }

  function notePathRow(): void {
    const v = pathValues();
    if (v === null) {
      pathRecorder.skip();
      return;
    }
    const i = pathIds.indexOf(contactBodyId);
    pathRecorder.note(live.flightSimSeconds, v, contactSimS, i < 0 ? -1 : 3 + 3 * i);
  }

  const _hullBox = new THREE.Box3();
  const _hullUp = new THREE.Vector3();
  const _hullCorner = new THREE.Vector3();
  const _hullOrigin = new THREE.Vector3();

  /**
   * How far the hull reaches past the ship's origin along one direction, metres.
   *
   * The box, the origin and the direction are all the SCENE's axes. `ship.group`
   * hangs off the bubble, whose transform is a rebase offset and the tilt onto
   * the frame's orbital plane, so a box taken in world metres against
   * `ship.group.position` in bubble metres reads the rebase instead of the hull -
   * kilometres of it, growing until the next rebase.
   */
  function hullReachM(dir: THREE.Vector3): number {
    _hullBox.setFromObject(deps.ship.group);
    if (_hullBox.isEmpty()) return 0;
    const o = deps.ship.group.getWorldPosition(_hullOrigin);
    let deepest = 0;
    for (let i = 0; i < 8; i++) {
      _hullCorner.set(
        (i & 1 ? _hullBox.max : _hullBox.min).x - o.x,
        (i & 2 ? _hullBox.max : _hullBox.min).y - o.y,
        (i & 4 ? _hullBox.max : _hullBox.min).z - o.z,
      );
      deepest = Math.min(deepest, _hullCorner.dot(dir));
    }
    return -deepest;
  }

  /**
   * Put a fresh contact down on the globe as it is DRAWN rather than on the mean
   * sphere the sweep meets, and on the hull's own keel rather than on the ship's
   * origin. Without this the ship hangs over the picture by the body's relief -
   * 1.6 km where Mars's first landing fell, 1.1 km on Luna.
   */
  function seatShipOnDrawnSurface(bodyId: string): void {
    if (SEAT_ON_DATUM) return;
    const at = live.simBodies?.worldAt(bodyId);
    if (!at) return;
    const w = simPos.world;
    const radiusM = atlasWorld()?.drawnSurfaceRadiusM(bodyId,
      { x: w.x - at.x, y: w.y - at.y, z: w.z - at.z }) ?? null;
    if (radiusM === null) return;
    _hullUp.set(w.x - at.x, w.y - at.y, w.z - at.z);
    if (_hullUp.lengthSq() === 0) return;
    _hullUp.normalize();
    seatOnGround(flight, radiusM + hullReachM(_hullUp));
  }

  /**
   * Where a resting ship sits against the globe as it is drawn: the radius it
   * rests at, the radius the mesh carries under it, how far the hull reaches
   * below its own origin, and what is left between the keel and that surface.
   * Null while the ship is flying. Read by `tools/preview/grounded-view-check.mjs`.
   */
  function groundSeatReading(): GroundSeatReading | null {
    const bodyId = flight.grounded;
    if (bodyId === null) return null;
    const at = live.simBodies?.worldAt(bodyId);
    if (!at) return null;
    const w = simPos.world;
    const dx = w.x - at.x;
    const dy = w.y - at.y;
    const dz = w.z - at.z;
    const shipRadiusM = Math.hypot(dx, dy, dz);
    const drawnRadiusM = atlasWorld()?.drawnSurfaceRadiusM(bodyId, { x: dx, y: dy, z: dz }) ?? null;
    if (drawnRadiusM === null || !(shipRadiusM > 0)) return null;
    _hullUp.set(dx, dy, dz).normalize();
    const reachM = hullReachM(_hullUp);
    return { bodyId, shipRadiusM, drawnRadiusM, hullReachM: reachM,
      keelFloatM: shipRadiusM - reachM - drawnRadiusM };
  }

  let lastContactReading: ContactReading | null = null;

  /** DEV only, `touchdown-ground-check.mjs`'s reading of the latest contact:
   *  where the sweep stopped the ship against the drawn ground, and what the
   *  seat then moved it by. Two rays, so never on a player's touchdown frame. */
  function noteContactReading(bodyId: string, speedMps: number, contactRadiusM: number): void {
    const o = flight.groundOffset;
    const drawnRadiusM = atlasWorld()?.drawnSurfaceRadiusM(bodyId, o) ?? null;
    const seatedRadiusM = Math.hypot(o.x, o.y, o.z);
    if (drawnRadiusM === null || !(seatedRadiusM > 0)) { lastContactReading = null; return; }
    _hullUp.set(o.x, o.y, o.z).normalize();
    lastContactReading = { bodyId, speedMps, contactRadiusM, drawnRadiusM, seatedRadiusM,
      hullReachM: hullReachM(_hullUp) };
  }

  /**
   * The drawn ground's radius out of a row's centre along one direction, with
   * the ship's own radius from that centre beside it. The seat's own reader,
   * asked while the ship is still FLYING, which is where `groundSeatReading`
   * says nothing: `tools/preview/grounded-view-check.mjs` samples it every frame
   * of a descent to see whether the hull ever passes through the ground it is
   * falling toward. No direction is the ship's own. Null on a shape-model body
   * until its shape has loaded.
   */
  function groundUnderReading(bodyId: string, dir: FlightVec | null):
  { shipRadiusM: number; drawnRadiusM: number; contactRadiusM: number | null } | null {
    const at = live.simBodies?.worldAt(bodyId);
    if (!at) return null;
    const w = simPos.world;
    const dx = w.x - at.x;
    const dy = w.y - at.y;
    const dz = w.z - at.z;
    const shipRadiusM = Math.hypot(dx, dy, dz);
    const under = dir ?? { x: dx, y: dy, z: dz };
    const drawnRadiusM = atlasWorld()?.drawnSurfaceRadiusM(bodyId, under) ?? null;
    if (drawnRadiusM === null) return null;
    // The datum the pilot panel's LOW POINT and DISTANCE are priced against.
    const row = bodyById(live.bodyRows, bodyId);
    const contact = row === undefined ? null : contactRadiusM(row, under.x, under.y, under.z);
    return { shipRadiusM, drawnRadiusM, contactRadiusM: contact };
  }

  /** Off the ground and into orbit about it, through the same parkInOrbit the debug API uses.
   *  The corvette's 0.49 m/s2 cannot beat Mars's 3.72, so nothing else gets a player off.
   *  It is also the way out of the ladder a landing hands the view to: the hull is what
   *  leaves, so the camera follows it back to the bridge rather than needing a verb of its own. */
  function attemptLaunch(): void {
    if (deps.game.engagement !== null) return;
    if (isMode('menu')) return;
    if (deps.landVerb.open && preHandoff()) return;
    const bodyId = flight.grounded;
    if (bodyId === null) return;
    const row = bodyById(live.bodyRows, bodyId);
    const name = (row?.display_name ?? bodyId).toUpperCase();
    const park = deps.pilot.parkInOrbit(bodyId, LAUNCH_ALTITUDE_M);
    if (!park.ok) {
      deps.warp.showNotice(`LAUNCH FAILED: NO ORBIT ABOUT ${name}`);
      return;
    }
    if (deps.landVerb.open) deps.landVerb.returnToShip();
    deps.warp.showNotice(`LAUNCH: ORBIT ABOUT ${name} AT `
      + `${formatDistanceForHud(LAUNCH_ALTITUDE_M)}`);
  }

  const _rebaseWas: FlightVec = { x: 0, y: 0, z: 0 };

  /**
   * The frame handoff: the body pulling hardest has changed, so the velocity that
   * was relative to the old one is rebased onto the new one.
   *
   * Nothing about the physics changes across this - the field `stepFlight` sees
   * is the same either way - so it is a pure change of what the numbers are
   * measured against, and the drawn position is untouched.
   */
  function rebaseFlightFrame(): void {
    const frameParentId = gravityField.frameParentId;
    if (frameParentId === frameParentWas) return;
    const wasId = frameParentWas;
    frameParentWas = frameParentId;
    if (live.simBodies === null) return;
    const wasV = wasId === null ? null : live.simBodies.velocityAt(wasId);
    const nowV = frameParentId === null ? null : live.simBodies.velocityAt(frameParentId);
    _rebaseWas.x = (wasV?.x ?? 0) - (nowV?.x ?? 0);
    _rebaseWas.y = (wasV?.y ?? 0) - (nowV?.y ?? 0);
    _rebaseWas.z = (wasV?.z ?? 0) - (nowV?.z ?? 0);
    flight.velocity.x += _rebaseWas.x;
    flight.velocity.y += _rebaseWas.y;
    flight.velocity.z += _rebaseWas.z;
    // The aim was commanded against the OLD parent's velocity, which the rebase
    // above has just moved by that parent's own travel: carrying it across makes
    // the handoff itself a turn nobody commanded, and a null aim is the ship's
    // own velocity - no gap and no spend - until the next turn commands one.
    if (!assistKeepAim) {
      flight.assistAim = null;
      flight.assistCatchUp = false;
    }
  }

  /**
   * Where the ship is drawn, in the world the registry is drawn in.
   *
   * The frame parent's own live place with the parent-relative state on top of
   * it. Both branches of `tick()` carry the parent's travel into this and step
   * the state before anything reads it, so the Atlas marker, the prediction's
   * near end and the pilot rows cannot disagree about where the ship is.
   */
  function shipWorldAt(): WorldVec {
    return simPos.world;
  }

  /**
   * The ship's velocity in the world the registry is drawn in.
   *
   * The ONE accessor anything outside the ship's own frame reads. `flight.velocity`
   * is the ship's velocity relative to the body pulling hardest, which is what
   * `applyAssist` steers and what SPD reports; composing the frame parent's own
   * velocity onto it is the only place a heliocentric ship velocity exists.
   */
  const _shipWorldVel: FlightVec = { x: 0, y: 0, z: 0 };
  function shipWorldVelocity(): FlightVec {
    const frameParentId = gravityField.frameParentId;
    const pv = frameParentId === null ? null : live.simBodies?.velocityAt(frameParentId) ?? null;
    _shipWorldVel.x = flight.velocity.x + (pv?.x ?? 0);
    _shipWorldVel.y = flight.velocity.y + (pv?.y ?? 0);
    _shipWorldVel.z = flight.velocity.z + (pv?.z ?? 0);
    return _shipWorldVel;
  }

  /**
   * Put the registry where the shared clock says it is.
   *
   * `seedJd` used to be written once, at boot, so the flight model's solar system
   * was nailed to the instant the page loaded while the Atlas drew the same rows
   * at the live clock and walked away from it. It now advances every frame, in
   * both of `tick()`'s branches, which is what makes the two views one system.
   * `?bodiespinned=1` puts the old epoch back.
   */
  function advanceSimBodies(): void {
    const jd = bodiesPinned && Number.isFinite(live.bootJd) ? live.bootJd : gameClock().jdTdb();
    live.seedJdPrev = Number.isFinite(live.seedJd) ? live.seedJd : jd;
    live.seedJd = jd;
    const places = live.simBodies?.places;
    if (places !== undefined) gravityField.snapshotPrev(places);
    live.simBodies?.update(jd, simPos.world);
    carrySeedOntoLateSets();
  }

  /**
   * Carry a ship still on its seeded orbit with its parent when that parent's
   * osculating sets are first drawn.
   *
   * A dropped first answer seeds the ship off the parent's secular fit, and the
   * retry lands a second or more later with Phobos 934 km from where the fit
   * drew it. `frameParentStep()` reads both of its epochs off the rows as they
   * now are, so it never sees that jump; this moves the ship by the parent's own
   * change of place and velocity at the one epoch, which keeps the offset the
   * seed gave it from the body it orbits and leaves its attitude, its bubble and
   * its assist aim as they were.
   */
  function carrySeedOntoLateSets(): void {
    const on = seedOnFit;
    if (on === null || live.simBodies === null) return;
    // Thrust moves the ship off the orbit the seed gave it, a grounded hull rests
    // on the body under it, and an open land verb's pin holds the ship to the
    // body LAND opened on.
    if (flight.deltaVSpent !== on.dvSpent || flight.grounded !== null || deps.landVerb.open) {
      seedOnFit = null;
      return;
    }
    const row = bodyById(live.bodyRows, on.parentId);
    const orbit = row?.orbit;
    const drawn = live.simBodies.worldAt(on.parentId);
    if (row === undefined || !orbit || orbit.sets === undefined || drawn === null) return;
    const now = heliocentric(row, live.bodyRows, live.seedJd);
    // A registry placed at an unchanged epoch keeps the place it had, so the
    // sets can be in the row a frame or more before they are drawn.
    if (Math.hypot(drawn.x - now.position.x * 1000, drawn.y - now.position.y * 1000,
      drawn.z - now.position.z * 1000) > 1) return;
    const { sets: _sets, ...fitOrbit } = orbit;
    const was = heliocentric({ ...row, orbit: fitOrbit }, live.bodyRows, live.seedJd);
    seedOnFit = null;
    simPos.advance(
      (now.position.x - was.position.x) * 1000,
      (now.position.y - was.position.y) * 1000,
      (now.position.z - was.position.z) * 1000,
    );
    // `flight.velocity` is relative to the frame parent, which moved with the
    // sets only if it is this row.
    if (gravityField.frameParentId !== on.parentId) {
      flight.velocity.x += (now.velocity.x - was.velocity.x) * 1000;
      flight.velocity.y += (now.velocity.y - was.velocity.y) * 1000;
      flight.velocity.z += (now.velocity.z - was.velocity.z) * 1000;
    }
    deps.warp.invalidateSweep();
    syncShipTransform();
  }

  /**
   * How far the frame's own parent moved between the last placement and this one.
   *
   * Taken from the ephemeris at both epochs rather than from `v * dt`, so it is
   * exact at any warp level: at a day a second one frame is 8,640 sim seconds and
   * a linear step off the velocity would cut the corner of Mars's orbit by
   * kilometres. The ship's drawn heliocentric place rides this; its own
   * parent-relative motion is what `stepFlight` integrated.
   *
   * The parent is the one the interval is about to be FLOWN in, which is why both
   * branches of `tick()` pick before they call this. The two are one statement -
   * the drawn place is the parent's own live centre plus the offset the ship
   * holds against it - and a carry taken on a parent the step then does not use
   * loses a frame of the two parents' velocity difference.
   */
  const _parentStep: FlightVec = { x: 0, y: 0, z: 0 };
  /**
   * Place the registry at the instant a load has just set the clock to.
   *
   * `frameParentStep()` carries the ship by however far its parent travelled
   * between the epoch the registry was last placed at and this frame's. A
   * restored save and a scenario restart both rewind the clock, so the first
   * frame after either, cruise's or a fight's, would otherwise carry the ship by
   * the parent's travel over a NEGATIVE interval - 206 km of Mars on a restart
   * nine seconds into a session. Placed here, that frame carries only what it
   * flies from the loaded instant.
   *
   * Before the registry lands there is nothing to move; `seedShipOrbit` places
   * the first epoch.
   *
   * Only for a clock move that also replaces the ship's state. One that leaves the
   * state alone (the Atlas probe's `setInstant`) needs the carry to keep the ship on its parent.
   */
  function placeFrameEpoch(): void {
    if (live.simBodies === null || rewindCarry) return;
    advanceSimBodies();
  }

  function frameParentStep(): FlightVec {
    _parentStep.x = 0;
    _parentStep.y = 0;
    _parentStep.z = 0;
    const frameParentId = gravityField.frameParentId;
    if (frameParentId === null || !Number.isFinite(live.seedJd) || !Number.isFinite(live.seedJdPrev)) {
      return _parentStep;
    }
    if (live.seedJd === live.seedJdPrev) return _parentStep;
    const row = bodyById(live.bodyRows, frameParentId);
    if (row === undefined) return _parentStep;
    const was = heliocentric(row, live.bodyRows, live.seedJdPrev).position;
    const now = heliocentric(row, live.bodyRows, live.seedJd).position;
    _parentStep.x = (now.x - was.x) * 1000;
    _parentStep.y = (now.y - was.y) * 1000;
    _parentStep.z = (now.z - was.z) * 1000;
    return _parentStep;
  }

  /**
   * A fight's frame, live or behind a menu: the registry at the clock and the
   * whole bubble carried by its parent's travel, in cruise's own order. A step
   * reads only `world - seed`, which the carry leaves where it was.
   *
   * The pick runs at most once a fight, on a frame with no frame parent yet.
   * Cruise always has one and a restart keeps it (`restartState()` in
   * main.ts), so that is only a fight opened before any parent was ever
   * picked. Past that one pick, `exitEngagement()`'s invariant holds:
   * `flight.velocity` and the delta-v total are frozen for the whole fight, so a
   * re-pick that flipped mid-fight and rebased against it would violate that
   * freeze and clear a commanded aim nobody asked to drop. The ship rides
   * whatever parent it entered the fight with; `refillFlightField()` keeps the
   * pulling set and the carry fresh without re-picking. The first cruise frame
   * after the fight picks the parent as cruise always does.
   */
  function rideFightFrame(): void {
    if (fightHeld) return;
    if (gravityField.frameParentId === null) {
      updateFlightField();
      rebaseFlightFrame();
    } else {
      refillFlightField();
    }
    advanceSimBodies();
    const carried = frameParentStep();
    simPos.carry(carried.x, carried.y, carried.z);
    simPos.sceneOffset(bubble.position);
    refillFlightField();
  }

  /**
   * The frame handoff: the body pulling hardest has changed, so the velocity that
   * was relative to the old one is rebased onto the new one.
   *
   * Nothing about the physics changes across this - the field `stepFlight` sees
   * is the same either way - so it is a pure change of what the numbers are
   * measured against, and the drawn position is untouched.
   */
  let frameParentWas: string | null = null;
  /**
   * The seeded orbit's parent while it stands on the secular fit the seed was
   * taken against, with the session delta-v at that seed. Cleared once the ship
   * is carried onto the parent's sets (`carrySeedOntoLateSets`), flown,
   * grounded, landed or placed anew.
   */
  let seedOnFit: { parentId: string; dvSpent: number } | null = null;
  /**
   * Declare the frame a freshly written velocity is already in. A seed or a park
   * chooses both at once, so the handoff must not then rebase what it just wrote.
   *
   * It is also the one signal a teleport leaves: `simPos.seedAt()` jumps the
   * drawn position outside `simPos.advance()`, and the warp drop's swept test
   * remembers the last position it read to sweep a segment against. Without
   * this, the segment from wherever the ship was before a seed to wherever it
   * lands after one reads as a frame of travel through anything between them.
   */
  function adoptFlightFrame(id: string | null): void {
    gravityField.frameParentId = id;
    frameParentWas = id;
    seedOnFit = null;
    deps.warp.invalidateSweep();
    // A seed or a park puts the ship somewhere of its own choosing, which is
    // never still resting on whatever it last landed on.
    flight.grounded = null;
    flight.contactSpeedMps = 0;
  }

  return {
    flightConfig,
    flight,
    gravityField,
    _frameCtx,
    assistSeconds,
    assistLive,
    stepFlightFrame,
    advanceSimBodies,
    frameParentStep,
    rideFightFrame,
    placeFrameEpoch,
    adoptFlightFrame,
    get frameParentWas() { return frameParentWas; },
    set frameParentWas(v: string | null) { frameParentWas = v; },
    get seedOnFit() { return seedOnFit; },
    set seedOnFit(v: { parentId: string; dvSpent: number } | null) { seedOnFit = v; },
    rebaseFlightFrame,
    contactRadiusM,
    noteStopLateSag,
    noteGroundContact,
    pathRecorder,
    get pathIds() { return pathIds; },
    set pathIds(v: readonly string[]) { pathIds = v; },
    pathValues,
    notePathRow,
    hullReachM,
    seatShipOnDrawnSurface,
    groundSeatReading,
    noteContactReading,
    get lastContactReading() { return lastContactReading; },
    groundUnderReading,
    attemptLaunch,
    shipWorldAt,
    shipWorldVelocity,
    slotSurfaceRadiusM,
    get slotSurfaceDir() { return _slotSurfaceDir; },
  };
}
